/**
 * パネルのデータ型定義
 */
type PanelData = {
  panel: HTMLElement;
  character: HTMLElement;
  visibleRatio: number;
  hideTimeoutId?: number;
};

/**
 * アプリケーションで使用する定数値
 */
const CONSTANTS = {
  TRANSITION_DURATION: 500,
  MIN_VISIBILITY_THRESHOLD: 0.1,
  INTERSECTION_THRESHOLDS: Array.from({ length: 21 }, (_, i) => i * 0.05),
} as const;

/**
 * ソリューションセクションの初期化を行う
 * パネルの監視とキャラクターの表示制御を開始する
 */
function solutions(): void {
  const solutionsPanels = document.querySelectorAll<HTMLElement>(".p-solutions__panel");
  if (solutionsPanels.length === 0) return;

  const panelsData = initializePanelsData(solutionsPanels);
  initializeObserver(panelsData);
}

/**
 * パネルデータの初期化
 * 各パネルのキャラクター要素を取得し、初期状態を設定する
 * @param panels - ソリューションパネルの要素リスト
 * @returns 初期化されたパネルデータの配列
 */
function initializePanelsData(panels: NodeListOf<HTMLElement>): PanelData[] {
  const panelsData = Array.from(panels).map((panel) => ({
    panel,
    character: panel.querySelector<HTMLElement>(".p-solutions__panel-character")!,
    visibleRatio: 0,
  }));

  panelsData.forEach((data) => {
    data.character.classList.add("is-hidden");
  });

  return panelsData;
}

/**
 * キャラクターの表示/非表示を制御
 * 表示状態の切り替えとタイマーの管理を行う
 * @param data - パネルデータ
 * @param isVisible - 表示するかどうか
 */
function handleCharacterVisibility(data: PanelData, isVisible: boolean): void {
  if (data.hideTimeoutId) {
    window.clearTimeout(data.hideTimeoutId);
    data.hideTimeoutId = undefined;
  }

  if (isVisible) {
    showCharacter(data.character);
  } else {
    hideCharacter(data);
  }
}

/**
 * キャラクターを表示状態にする
 * フェードインアニメーションを適用
 * @param character - キャラクター要素
 */
function showCharacter(character: HTMLElement): void {
  character.style.visibility = "visible";
  requestAnimationFrame(() => {
    character.classList.remove("is-hidden", "is-hiding");
    character.classList.add("is-visible");
  });
}

/**
 * キャラクターを非表示状態にする
 * フェードアウトアニメーションを適用
 * @param data - パネルデータ
 */
function hideCharacter(data: PanelData): void {
  data.character.classList.remove("is-visible");
  data.character.classList.add("is-hiding");

  data.hideTimeoutId = window.setTimeout(() => {
    data.character.classList.remove("is-hiding");
    data.character.classList.add("is-hidden");
    data.character.style.visibility = "hidden";
    data.hideTimeoutId = undefined;
  }, CONSTANTS.TRANSITION_DURATION);
}

/**
 * Intersection Observerの初期化と設定
 * パネルの視認性を監視し、キャラクターの表示を制御する
 * @param panelsData - パネルデータの配列
 */
function initializeObserver(panelsData: PanelData[]): void {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const panelData = panelsData.find((data) => data.panel === entry.target);
        if (panelData) {
          panelData.visibleRatio = entry.intersectionRatio;
        }
      });

      const mostVisiblePanel = findMostVisiblePanel(panelsData);

      panelsData.forEach((data) => {
        handleCharacterVisibility(
          data,
          data === mostVisiblePanel && mostVisiblePanel.visibleRatio > CONSTANTS.MIN_VISIBILITY_THRESHOLD,
        );
      });
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: CONSTANTS.INTERSECTION_THRESHOLDS,
    },
  );

  panelsData.forEach((data) => observer.observe(data.panel));
}

/**
 * 最も視認性の高いパネルを特定
 * @param panelsData - パネルデータの配列
 * @returns 最も視認性の高いパネルデータ
 */
function findMostVisiblePanel(panelsData: PanelData[]): PanelData {
  return panelsData.reduce((prev, current) => (current.visibleRatio > prev.visibleRatio ? current : prev));
}

export { solutions };
